import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import view from '../views/users.js';
import form from '../forms/users.js';
import * as types from '../types.js';

const LOCKOUT_DAYS = 30;

function lockoutExpirationIso() {
	const d = new Date();
	d.setDate(d.getDate() + LOCKOUT_DAYS);
	return d.toISOString();
}

const users = function()
{

	let users_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_groups, this.queryAll()).then((data) => db.set('groups', data).catch(error => notify(error)));
			api.call(api.list_users, this.queryAll()).then((data) => db.set('users', data).catch(() => {}));
		},
		async queryData(query)
		{
			return await api.call(api.list_users, query);
		},
		async loadRecord(id)
		{
			const data = await api.call(api.select_user, ...JSON.parse(id));
			if (data && typeof data === 'object') {
				data.user_status = data.is_disabled ? 'Inativo' : (data.is_blocked ? 'Bloqueado' : 'Ativo');
			}
			return data;
		},
		editRecord(data)
		{
			const self = this;
			const descriptor = {
				html: form.html,
				fields: form.fields,
				form: {
					...form.form,
					afterEditSetValues(fs) {
						fs._uac_pending_data = data || null;
						form.form.afterEditSetValues?.(fs);
					},
				},
			};
			let fs = formset.create(descriptor, async (updated_data) => {
				const access = fs._uac?.readAccessFields?.() || {};
				Object.assign(updated_data, access);
				if (await self.saveRecordWrapper(updated_data, data))
					fs.destroy();
			});
			fs.init = (() => {
				fs.form.eraseValues();
				Object.getPrototypeOf(fs).init.call(fs, true);
				if (data) {
					fs.form.setValues(data);
					fs.form.refresh();
					setTimeout(() => {
						fs.form.refresh();
						descriptor.form.afterEditSetValues?.(fs);
					}, 300);
				} else {
					fs.form.refresh();
					descriptor.form.afterEditSetValues?.(fs);
				}
			})();
		},
		async saveRecord(data, old_data)
		{
			let _record = {
				name: data.name,
				email: data.email,
				phone: data.phone,
				username: data.username,
				groups: data.groups,
				access_control_enabled: !!data.access_control_enabled,
				work_timezone: data.work_timezone || 'America/Sao_Paulo',
				work_schedule: data.work_schedule || {},
				manager_user_id: data.manager_user_id || null,
				collector_only: !!data.collector_only,
				access_override_until: data.access_override_until || null,
			};
			if (old_data) {
				const current = await api.call(api.select_user, data.id);
				if (current && typeof current === 'object') {
					if ('is_disabled' in current) _record.is_disabled = current.is_disabled;
					if ('lockout_expiration' in current) _record.lockout_expiration = current.lockout_expiration;
				}
				return await api.call(api.update_user, data.id, _record);
			}
			return await api.call(api.insert_user, _record);
		},
		async setDisabled(disabled) {
			const ids = await this.getSelectedIds(['id']);
			if (!ids.length) return notify('Selecione ao menos um usuário.', 'Usuários');
			const action = disabled ? 'Inativar' : 'Ativar';
			ask(`Confirma ${action} ${ids.length} usuário(s)?`, action, async (answer) => {
				if (answer !== 'Y') return;
				for (const [id] of ids) {
					const resp = await api.call(api.update_user, id, { is_disabled: !!disabled });
					if (resp == null) { notify('Falha ao atualizar usuário.', 'Usuários'); return; }
				}
				this.loadPage();
			});
		},
		async setBlocked(blocked) {
			const ids = await this.getSelectedIds(['id']);
			if (!ids.length) return notify('Selecione ao menos um usuário.', 'Usuários');
			const action = blocked ? 'Bloquear' : 'Desbloquear';
			ask(`Confirma ${action} ${ids.length} usuário(s)?`, action, async (answer) => {
				if (answer !== 'Y') return;
				const payload = blocked ? { lockout_expiration: lockoutExpirationIso() } : { lockout_expiration: null };
				for (const [id] of ids) {
					const resp = await api.call(api.update_user, id, payload);
					if (resp == null) { notify('Falha ao atualizar usuário.', 'Usuários'); return; }
				}
				this.loadPage();
			});
		},
		async openMyOvertime() {
			const data = await api.call(api.list_user_overtime_requests, '?mine=1&status=pending&limit=100');
			const items = Array.isArray(data) ? data : [];
			if (!items.length) return notify('Nenhuma HE pendente para você como gestor.', 'Horas extras');
			const lines = items.map((r) =>
				`• ${r.user_name || r.user_username}: ${r.from_at} → ${r.to_at} (${r.reason || 'sem motivo'})`
			).join('\n');
			ask(`Pendentes (${items.length}):\n${lines}\n\nAprovar a primeira da lista?`, 'Horas extras', async (answer) => {
				if (answer !== 'Y') return;
				const first = items[0];
				const resp = await api.call(api.decide_user_overtime_request, first.id, { status: 'approved' });
				if (resp == null) notify('Falha ao aprovar.', 'Horas extras');
				else notify('HE aprovada. Abra o usuário para gerenciar as demais.', 'Horas extras');
			});
		},
	});

	let fieldspecs = [
		{ index: 1, field: 'id', label: 'Usuário', types: [], placeholder: '', title: 'Identificador do usuário' },
		{ index: 2, field: 'name', label: 'Nome', types: [types.name()], placeholder: '', title: 'Nome do usuário' },
		{ index: 3, field: 'email', label: 'Email', types: [types.email()], placeholder: '', title: 'Email para reset de senha' },
		{ index: 4, field: 'phone', label: 'Fone', types: [types.phone()], placeholder: '+00 (00) 0000-0000', title: 'Telefone de contato' },
		{ index: 5, field: 'username', label: 'Username', types: [types.username()], placeholder: '', title: 'Nome de usuário para acesso ao sistema' },
		{ index: 6, field: 'user_status', label: 'Status', types: [], placeholder: '', title: 'Status do usuário' },
		{ index: 7, field: 'is_blocked', label: 'Bloqueado', types: [], placeholder: '', title: 'Usuário bloqueado (SIM/NAO)' },
	];

	let operations = [
		{ hash: '#user-disable',   label: 'Inativar'      },
		{ hash: '#user-enable',    label: 'Ativar'        },
		{ hash: '#user-block',     label: 'Bloquear'      },
		{ hash: '#user-unblock',   label: 'Desbloquear'    },
		{ hash: '#user-overtime',  label: 'HE pendentes (gestor)' },
	];

	router.set('#user-disable',  users_crud.setDisabled.bind(users_crud, true));
	router.set('#user-enable',   users_crud.setDisabled.bind(users_crud, false));
	router.set('#user-block',    users_crud.setBlocked.bind(users_crud, true));
	router.set('#user-unblock',  users_crud.setBlocked.bind(users_crud, false));
	router.set('#user-overtime', users_crud.openMyOvertime.bind(users_crud));

	users_crud.run(
		'users',
		'Usuários',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		[]
	);

};

export default users;
